import { useState, useEffect } from "react";
import { Users, Copy, Check, UserPlus, Clock, XCircle, Shield, ChevronRight, UserX, Share2, MessageCircle, Dumbbell, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useFriendships, Friend } from "@/hooks/useFriendships";
import { useReferral } from "@/hooks/useReferral";
import { FriendProfileModal } from "@/components/FriendProfileModal";
import { calculateTier, TIER_COLORS, EvolveTier } from "@/types/evolve";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { format, isToday, isYesterday } from "date-fns";

interface FriendsSectionProps {
  userId: string;
  currentUserStats?: { totalWorkouts: number; daysActive: number; badges: number };
}

interface FriendWithStats extends Friend {
  totalWorkouts?: number;
}

interface FriendActivity {
  id: string;
  friendId: string;
  friendName: string;
  friendAvatar: string | null;
  type: 'workout';
  workoutType: string;
  completedAt: string;
}

export function FriendsSection({ userId, currentUserStats }: FriendsSectionProps) {
  const { referralCode } = useReferral();
  const {
    friends,
    pendingRequests,
    outgoingRequests,
    blockedUsers,
    loading,
    sendFriendRequest,
    acceptRequest,
    rejectRequest,
    blockUser,
    unblockUser,
    removeFriend,
  } = useFriendships(userId);

  const [copied, setCopied] = useState(false);
  const [friendCode, setFriendCode] = useState("");
  const [addingFriend, setAddingFriend] = useState(false);
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState<FriendWithStats | null>(null);
  const [showBlocked, setShowBlocked] = useState(false);
  const [friendsWithStats, setFriendsWithStats] = useState<FriendWithStats[]>([]);
  const [friendActivity, setFriendActivity] = useState<FriendActivity[]>([]);

  // Fetch workout counts and recent activity for friends
  useEffect(() => {
    const fetchFriendStats = async () => {
      if (friends.length === 0) {
        setFriendsWithStats([]);
        setFriendActivity([]);
        return;
      }

      const friendIds = friends.map(f => f.friendId);
      
      // Get workout counts
      const { data: workouts } = await supabase
        .from('workout_sessions')
        .select('user_id, workout_type, completed_at')
        .in('user_id', friendIds)
        .order('completed_at', { ascending: false });

      const countMap: Record<string, number> = {};
      workouts?.forEach(row => {
        countMap[row.user_id] = (countMap[row.user_id] || 0) + 1;
      });

      setFriendsWithStats(friends.map(f => ({
        ...f,
        totalWorkouts: countMap[f.friendId] || 0,
      })));

      // Get recent activity (last 5 workouts from all friends)
      const recentActivity: FriendActivity[] = [];
      const seen = new Set<string>();
      
      workouts?.slice(0, 10).forEach(workout => {
        if (seen.has(workout.user_id)) return;
        seen.add(workout.user_id);
        
        const friend = friends.find(f => f.friendId === workout.user_id);
        if (friend) {
          recentActivity.push({
            id: `${workout.user_id}-${workout.completed_at}`,
            friendId: friend.friendId,
            friendName: friend.friendName,
            friendAvatar: friend.friendAvatar,
            type: 'workout',
            workoutType: workout.workout_type || 'Workout',
            completedAt: workout.completed_at,
          });
        }
      });

      setFriendActivity(recentActivity.slice(0, 3));
    };

    fetchFriendStats();
  }, [friends]);

  const handleCopyCode = async () => {
    if (!referralCode) return;
    await navigator.clipboard.writeText(referralCode);
    setCopied(true);
    toast.success("Code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async (platform: 'whatsapp' | 'sms' | 'native') => {
    if (!referralCode) return;
    const message = `Add me on Evolve! My friend code is: ${referralCode}`;
    
    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
        break;
      case 'sms':
        window.open(`sms:?body=${encodeURIComponent(message)}`, '_blank');
        break;
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share({ text: message });
          } catch (e) {
            // User cancelled
          }
        } else {
          handleCopyCode();
        }
        break;
    }
  };

  const handleAddFriend = async () => {
    if (!friendCode.trim()) return;
    setAddingFriend(true);
    const success = await sendFriendRequest(friendCode.trim().toUpperCase());
    if (success) {
      setFriendCode("");
      setShowAddFriend(false);
    }
    setAddingFriend(false);
  };

  const formatActivityTime = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMM d');
  };

  return (
    <section className="glass-card rounded-2xl p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          <h3 className="font-display font-bold">Friends</h3>
          {friends.length > 0 && (
            <span className="text-xs text-muted-foreground">({friends.length})</span>
          )}
        </div>
        {pendingRequests.length > 0 && (
          <Badge variant="destructive" className="animate-pulse text-xs">
            {pendingRequests.length} pending
          </Badge>
        )}
      </div>

      {/* Friend Code - Compact */}
      {referralCode && (
        <div className="bg-secondary/40 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Your code</p>
              <code className="text-base font-mono font-bold text-primary">{referralCode}</code>
            </div>
            <Button size="icon" variant="ghost" onClick={handleCopyCode} className="h-8 w-8">
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex gap-1.5">
            <Button size="sm" variant="secondary" onClick={() => handleShare('whatsapp')} className="flex-1 h-7 text-[10px]">
              <MessageCircle className="w-3 h-3 mr-1" />WhatsApp
            </Button>
            <Button size="sm" variant="secondary" onClick={() => handleShare('sms')} className="flex-1 h-7 text-[10px]">
              <MessageCircle className="w-3 h-3 mr-1" />SMS
            </Button>
            <Button size="sm" variant="secondary" onClick={() => handleShare('native')} className="flex-1 h-7 text-[10px]">
              <Share2 className="w-3 h-3 mr-1" />Share
            </Button>
          </div>
        </div>
      )}

      {/* Friend Activity Feed */}
      {friendActivity.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
            <Flame className="w-3.5 h-3.5 text-orange-500" />
            Recent Activity
          </h4>
          <div className="space-y-1.5">
            {friendActivity.map((activity) => (
              <div
                key={activity.id}
                className="flex items-center gap-2 bg-secondary/20 rounded-lg p-2 text-xs"
              >
                <div className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold bg-gradient-to-br overflow-hidden shrink-0",
                  TIER_COLORS[calculateTier(0).currentTier]
                )}>
                  {activity.friendAvatar ? (
                    <img src={activity.friendAvatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    activity.friendName.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{activity.friendName}</span>
                  <span className="text-muted-foreground"> completed </span>
                  <span className="capitalize">{activity.workoutType}</span>
                </div>
                <span className="text-[10px] text-muted-foreground shrink-0">
                  {formatActivityTime(activity.completedAt)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Requests */}
      {pendingRequests.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            Friend Requests ({pendingRequests.length})
          </h4>
          <div className="space-y-2">
            {pendingRequests.map((request) => (
              <div key={request.id} className="bg-secondary/30 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold bg-gradient-to-br overflow-hidden",
                    TIER_COLORS[calculateTier(0).currentTier]
                  )}>
                    {request.friendAvatar ? (
                      <img src={request.friendAvatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                      request.friendName.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div>
                    <span className="text-sm font-medium">{request.friendName}</span>
                    <p className="text-[10px] text-muted-foreground">wants to be your friend</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    className="flex-1 h-8 text-xs bg-green-600 hover:bg-green-700 text-white" 
                    onClick={() => acceptRequest(request.id)}
                  >
                    <Check className="w-3.5 h-3.5 mr-1" />
                    Accept
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1 h-8 text-xs" 
                    onClick={() => rejectRequest(request.id)}
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" />
                    Decline
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-8 px-2 text-xs text-destructive hover:bg-destructive/10" 
                    onClick={() => blockUser(request.id, request.friendId)}
                    title="Block this user"
                  >
                    <Shield className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Outgoing Requests - Compact */}
      {outgoingRequests.length > 0 && (
        <div className="space-y-1.5">
          <h4 className="text-xs text-muted-foreground">Sent ({outgoingRequests.length})</h4>
          {outgoingRequests.map((request) => (
            <div key={request.id} className="flex items-center gap-2 bg-secondary/20 rounded-lg p-2 opacity-60">
              <div className={cn(
                "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold bg-gradient-to-br overflow-hidden",
                TIER_COLORS[calculateTier(0).currentTier]
              )}>
                {request.friendAvatar ? (
                  <img src={request.friendAvatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  request.friendName.charAt(0).toUpperCase()
                )}
              </div>
              <span className="text-sm">{request.friendName}</span>
              <span className="text-[10px] text-muted-foreground ml-auto">Pending</span>
            </div>
          ))}
        </div>
      )}

      {/* Friends List - Compact */}
      {friendsWithStats.length > 0 && (
        <div className="space-y-1.5">
          <h4 className="text-xs font-semibold text-muted-foreground">Your Friends</h4>
          {friendsWithStats.map((friend) => {
            const tierInfo = calculateTier(friend.totalWorkouts || 0);
            return (
              <button
                key={friend.id}
                onClick={() => setSelectedFriend(friend)}
                className="w-full flex items-center justify-between bg-secondary/30 hover:bg-secondary/50 rounded-lg p-2.5 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <div className={cn(
                    "w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold bg-gradient-to-br overflow-hidden",
                    TIER_COLORS[tierInfo.currentTier as EvolveTier]
                  )}>
                    {friend.friendAvatar ? (
                      <img src={friend.friendAvatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                      friend.friendName.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium leading-tight">{friend.friendName}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className={cn(
                        "px-1.5 py-0.5 rounded text-[9px] font-semibold bg-gradient-to-r",
                        TIER_COLORS[tierInfo.currentTier as EvolveTier]
                      )}>
                        {tierInfo.currentTier}
                      </span>
                      <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                        <Dumbbell className="w-2.5 h-2.5" />{friend.totalWorkouts || 0}
                      </span>
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      )}

      {/* Add Friend */}
      {showAddFriend ? (
        <div className="space-y-2">
          <Input
            placeholder="Enter friend code"
            value={friendCode}
            onChange={(e) => setFriendCode(e.target.value.toUpperCase())}
            className="font-mono h-9 text-sm"
          />
          <div className="flex gap-2">
            <Button onClick={handleAddFriend} disabled={!friendCode.trim() || addingFriend} className="flex-1 h-8 text-sm">
              {addingFriend ? "Adding..." : "Add"}
            </Button>
            <Button variant="outline" onClick={() => { setShowAddFriend(false); setFriendCode(""); }} className="h-8 text-sm">
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" className="w-full h-9" onClick={() => setShowAddFriend(true)}>
          <UserPlus className="w-4 h-4 mr-2" />
          Add Friend by Code
        </Button>
      )}

      {/* Blocked Users - Minimal */}
      {blockedUsers.length > 0 && (
        <div className="pt-2 border-t border-border/20">
          <button onClick={() => setShowBlocked(!showBlocked)} className="text-[10px] text-muted-foreground hover:text-foreground">
            {showBlocked ? "Hide" : "Show"} blocked ({blockedUsers.length})
          </button>
          {showBlocked && (
            <div className="mt-2 space-y-1">
              {blockedUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between bg-secondary/10 rounded p-1.5 text-xs">
                  <div className="flex items-center gap-1.5">
                    <UserX className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">{user.friendName}</span>
                  </div>
                  <Button size="sm" variant="ghost" className="h-5 text-[10px] px-2" onClick={() => unblockUser(user.id)}>
                    Unblock
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && friends.length === 0 && pendingRequests.length === 0 && outgoingRequests.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-1">
          No friends yet. Share your code to connect!
        </p>
      )}

      {/* Friend Profile Modal */}
      <FriendProfileModal
        isOpen={!!selectedFriend}
        onClose={() => setSelectedFriend(null)}
        friendId={selectedFriend?.friendId || ""}
        friendName={selectedFriend?.friendName || ""}
        friendAvatar={selectedFriend?.friendAvatar}
        friendshipId={selectedFriend?.id}
        onRemoveFriend={removeFriend}
        currentUserStats={currentUserStats}
      />
    </section>
  );
}
